/**
 * @file: PumpInterfaceSettingsPage.js
 */
import React from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'
import { injectIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import Spinner from '../../util/spinner-shim'
import NavigationBar from '../partials/NavigationBar'
import Footer from '../partials/Footer'
import withAppStore from '../../util/withAppStore'

import AppStore from '../../stores/AppStore'
import AppDispatcher from '../../dispatchers/AppDispatcher'
import statusCalculate from 'altrac-application-status'

import styles from '../../../css/devicePage.module.css'

const dispatch = (action, obj = {}) => AppDispatcher.dispatch({ action, ...obj })

const PumpInterfaceSettings = function PumpInterfaceSettings ({ device, saved }) {
  const {
    address_alias: addressAlias,
    physical: {
      deviceNumber,
    } = {},
  } = device || {}

  const focusInElement = (id) => {
    const element = document.getElementById(id)
    if (element) {
      element.focus()
    }
  }

  const handleChange = (event, setter) => {
    const { target: { value } } = event
    setter(value)
  }

  const subject = device
    ? (
      <div className={styles.pageWidth}>
        <div>
          <div className={styles.pageContainer}>
            <div>
              <div className={styles.sectionName}>
                <h2>Pump Interface Settings</h2>
              </div>
              <div className={styles.sectionName}>
                <h3>Device: {addressAlias || deviceNumber}</h3>
              </div>
              <div className={styles.sectionName}>
                <h3>Status: {saved ? 'Saved' : 'Not Saved'}</h3>
              </div>
            </div>
          </div>
        </div>
      </div>
      )
    : (
      <div className='loading'>
        <Spinner name='double-bounce' />
      </div>
      )

  return subject
}

function PumpInterfaceSettingsPage (props) {
  const navigate = useNavigate()
  const { customerID, deviceID } = useParams()

  const [state, setState] = React.useState({
    saved: props.data?.deviceSettings?.saved || false,
  })

  const statusUpdateOnce = React.useRef(true)

  const appStoreChangeListener = React.useCallback(() => {
    const { data: { deviceSettings: { saved } } } = AppStore
    setState(prevState => ({ ...prevState, saved }))
  }, [])

  const backToDevice = React.useCallback(() => {
    navigate(`/${customerID}/pump/${deviceID}`)
  }, [customerID, deviceID, navigate])

  const getNewData = React.useCallback(() => {
    dispatch('get-device', { deviceID })
    AppStore.addChangeListener(appStoreChangeListener)
  }, [deviceID, appStoreChangeListener])

  const changeHandler = React.useCallback(() => {
    getNewData()
  }, [getNewData])

  React.useEffect(() => {
    getNewData()

    return () => {
      dispatch('unload-device-status')
      dispatch('reset-new-settings')
      AppStore.removeChangeListener(appStoreChangeListener)
    }
  }, [getNewData, appStoreChangeListener])

  React.useEffect(() => {
    const { data: { lookupReady, lookup } = {} } = props || {}
    const device = lookup[deviceID]

    if (lookupReady && device && statusUpdateOnce.current) {
      statusUpdateOnce.current = false
      const status = statusCalculate(device)
      dispatch('load-device-status', { status })
      dispatch('initialize-new-settings', { device })
    }
  }, [props.data?.lookupReady, props.data?.lookup, deviceID])

  const {
    auth: { logout },
    data: {
      lookupReady,
      lookup,
    } = {},
    pages: {
      PumpInterfaceSettingsPage: title = 'Pump Interface Settings',
    },
    route: {
      path,
    } = {},
  } = props

  const { saved } = state

  const device = lookupReady && lookup[deviceID]
  const {
    address_alias: addressAlias,
    physical: {
      deviceNumber,
    } = {},
  } = device || {}

  const subject = device
    ? (
      <div className={styles.pageWidth}>
        <div>
          <div className={styles.pageContainer}>
            <div>
              <div className={styles.sectionName}>
                <h2>Pump Interface Settings</h2>
              </div>
              <div className={styles.sectionName}>
                <h3>Device: {addressAlias || deviceNumber}</h3>
              </div>
              <div className={styles.sectionName}>
                <h3>Status: {saved ? 'Saved' : 'Not Saved'}</h3>
              </div>
            </div>
          </div>
        </div>
      </div>
      )
    : (
      <div className='loading'>
        <Spinner name='double-bounce' />
      </div>
      )

  return (
    <div>
      <Helmet><title>{title}</title></Helmet>
      <NavigationBar
        backFunc={backToDevice}
        name={title}
        page={path}
        logOut={() => logout(navigate)}
      />
      {subject}
      <Footer />
    </div>
  )
}

PumpInterfaceSettingsPage.propTypes = {
  auth: PropTypes.object,
  data: PropTypes.object,
  pages: PropTypes.object,
  route: PropTypes.object,
}

export default withAppStore(injectIntl(PumpInterfaceSettingsPage))
